export const DEFAULT_WORKING_HOURS = {
  enabled: false,
  days: [0, 1, 2, 3, 4],
  start: '09:00',
  end: '18:00',
};

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function normalizeWorkingHours(value) {
  const source = value && typeof value === 'object' ? value : {};
  const days = Array.isArray(source.days)
    ? [...new Set(source.days.filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))].sort((a, b) => a - b)
    : [...DEFAULT_WORKING_HOURS.days];
  const start = /^\d{2}:\d{2}/.test(source.start || '') ? String(source.start).slice(0, 5) : DEFAULT_WORKING_HOURS.start;
  const end = /^\d{2}:\d{2}/.test(source.end || '') ? String(source.end).slice(0, 5) : DEFAULT_WORKING_HOURS.end;
  return {
    enabled: Boolean(source.enabled),
    days,
    start,
    end,
  };
}

export function describeWorkingHours(value) {
  const hours = normalizeWorkingHours(value);
  if (!hours.enabled) return 'Recording at any hour';
  const names = hours.days.map((day) => DAY_LABELS[day]).join(', ');
  return `Recording ${names}, ${hours.start}–${hours.end} Israel time`;
}

export function workingHoursProblem(value) {
  const hours = normalizeWorkingHours(value);
  if (!hours.enabled) return '';
  if (hours.days.length === 0) return 'Choose at least one working day.';
  if (hours.start === hours.end) return 'The working day cannot start and end at the same time.';
  return '';
}

export function WorkingHoursFields({ value, onChange, idPrefix = 'hours' }) {
  const hours = normalizeWorkingHours(value);
  const problem = workingHoursProblem(hours);
  const set = (patch) => onChange({ ...hours, ...patch });
  const toggleDay = (day) => {
    const days = hours.days.includes(day) ? hours.days.filter((item) => item !== day) : [...hours.days, day].sort((a, b) => a - b);
    set({ days });
  };

  return (
    <div className="space-y-3">
      <label className="flex items-start gap-2 text-sm text-gray-800">
        <input
          id={`${idPrefix}-enabled`}
          type="checkbox"
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          checked={hours.enabled}
          onChange={(event) => set({ enabled: event.target.checked })}
        />
        <span>
          <span className="font-medium">Limit recording to working hours</span>
          <span className="mt-0.5 block text-gray-500">
            Outside these days and hours, agents do not record. Times are Israel time.
          </span>
        </span>
      </label>
      <div>
        <p id={`${idPrefix}-days-label`} className="text-sm font-medium text-gray-700">
          Working days
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-labelledby={`${idPrefix}-days-label`}>
          {DAY_LABELS.map((label, day) => {
            const on = hours.days.includes(day);
            return (
              <button
                key={label}
                type="button"
                aria-pressed={on}
                onClick={() => toggleDay(day)}
                className={`h-8 min-w-11 rounded-md border px-2 text-xs font-medium ${
                  on
                    ? 'border-primary-600 bg-primary-50 text-primary-800'
                    : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-gray-700" htmlFor={`${idPrefix}-start`}>
            From
            <input
              id={`${idPrefix}-start`}
              type="time"
              value={hours.start}
              onChange={(event) => set({ start: event.target.value.slice(0, 5) })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </label>
          <label className="block text-sm font-medium text-gray-700" htmlFor={`${idPrefix}-end`}>
            Until
            <input
              id={`${idPrefix}-end`}
              type="time"
              value={hours.end}
              onChange={(event) => set({ end: event.target.value.slice(0, 5) })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </label>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          Recording stops at the end time. A later end than the start continues into the next morning.
        </p>
        {problem ? <p className="mt-2 text-sm text-red-700">{problem}</p> : null}
      </div>
    </div>
  );
}
